import { studentTimetablePath, staffTimetablePath, type TimetableSource } from "./timetableEndpoints";
import { isAxiosError } from "axios";
import { portalApi } from "@services/api/portalApi";

export type TimetableFetchOptions = {
	date?: string;
	source?: TimetableSource;
};

export async function getStudentTimetable(
	studentNo: number,
	options?: TimetableFetchOptions,
): Promise<TimetableWeek | null> {
	try {
		const response = await portalApi.get<TimetableWeek>(
			studentTimetablePath(studentNo, options?.source),
			{
				params: {
					date: options?.date,
				},
			},
		);
		return response.data;
	} catch (error) {
		if (isAxiosError(error) && error.response?.data?.error === "timetable_unavailable") return null;
		console.error("Error fetching student timetable:", error);
		throw error;
	}
}

export async function getStaffTimetable(
	staffInitials: string,
	options?: TimetableFetchOptions,
): Promise<TimetableWeek> {
	try {
		const response = await portalApi.get<TimetableWeek>(
			staffTimetablePath(staffInitials, options?.source),
			{
				params: {
					date: options?.date,
				},
			},
		);
		return response.data;
	} catch (error) {
		console.error("Error fetching staff timetable:", error);
		throw error;
	}
}

export async function getRoomTimetable(
	roomCode: string,
	options?: TimetableFetchOptions,
): Promise<TimetableWeek> {
	try {
		const response = await portalApi.get<TimetableWeek>(
			`/timetables/room/${encodeURIComponent(roomCode)}`,
			{
				params: {
					date: options?.date,
				},
			},
		);
		return response.data;
	} catch (error) {
		console.error("Error fetching room timetable:", error);
		throw error;
	}
}

export async function getClassTimetable(classId: number, options?: TimetableFetchOptions): Promise<TimetableWeek> {
    const response = await portalApi.get<TimetableWeek>(`/timetables/class/${classId}`, {
        params: { date: options?.date },
    });
    return response.data;
}

export type TimetableSlot = {
	class: {
		id: number; // 21592
		code: string; //4;BS8;5E
		name: string; // Biology - should be short, concise, like the subject name rather than e.g. Biology A Level
		abbrivatedName?: string; // optional, useful for long names (full names available on hover)
	};
	teacher?: {
		id: number; // 4092
		initials: string; // KLM
		firstName: string; // Katie
		surname: string; // Smith
	};
	room?: string; // 402
	metadata?: {
		attendance?: number; // 0.93
	};
};

export type TimetableDay = {
	slot1: TimetableSlot[];
	slot2: TimetableSlot[];
	slot3: TimetableSlot[]; // break
	slot4: TimetableSlot[];
	slot5: TimetableSlot[];
	slot6: TimetableSlot[];
	slot7: TimetableSlot[]; // lunch
	slot8: TimetableSlot[]; // lunch
	slot9: TimetableSlot[];
	slot10: TimetableSlot[];
	slot11: TimetableSlot[];
	slot12?: TimetableSlot[]; // optional slot, hidden if no days contain slot12
};

export type TimetableWeek = {
	monday: TimetableDay;
	tuesday: TimetableDay;
	wednesday: TimetableDay;
	thursday: TimetableDay;
	friday: TimetableDay;
};

type Timetable = {
	type: "staff" | "student" | "room" | "class";
	week: TimetableWeek;
};

/* single /timetable/{type}/{id} endpoint
    type: 'staff' | 'student' | 'room' | 'class'
    id: string // staff initials, student number, room code, or class ID depending on the type
*/

const studentTimetable = {
	monday: {
		slot1: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
				metadata: { attendance: 1.0 },
			},
		],
		slot2: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
				metadata: { attendance: 1.0 },
			},
		],
		slot3: [],
		slot4: [],
		slot5: [
			{
				class: { id: 22044, code: "2;SKN;C1", name: "Study Skills" },
				teacher: {
					id: 781,
					initials: "NTW",
					firstName: "Neil",
					surname: "Winstone",
				},
				room: "317",
				metadata: { attendance: 1.0 },
			},
		],
		slot6: [],
		slot7: [],
		slot8: [],
		slot9: [],
		slot10: [
			{
				class: { id: 21437, code: "4;ECB;B1", name: "Economics" },
				teacher: {
					id: 3835,
					initials: "LMF",
					firstName: "Laura",
					surname: "Free",
				},
				room: "208",
				metadata: { attendance: 1.0 },
			},
		],
		slot11: [
			{
				class: { id: 21437, code: "4;ECB;B1", name: "Economics" },
				teacher: {
					id: 4011,
					initials: "OAS",
					firstName: "Stefania",
					surname: "Spinu",
				},
				room: "208",
				metadata: { attendance: 1.0 },
			},
		],
	},
	tuesday: {
		slot1: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
				metadata: { attendance: 0.75 },
			},
		],
		slot2: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
				metadata: { attendance: 0.75 },
			},
		],
		slot3: [],
		slot4: [
			{
				class: { id: 21437, code: "4;ECB;B1", name: "Economics" },
				teacher: {
					id: 3835,
					initials: "LMF",
					firstName: "Laura",
					surname: "Free",
				},
				room: "208",
				metadata: { attendance: 1.0 },
			},
		],
		slot5: [
			{
				class: { id: 21437, code: "4;ECB;B1", name: "Economics" },
				teacher: {
					id: 3835,
					initials: "LMF",
					firstName: "Laura",
					surname: "Free",
				},
				room: "208",
				metadata: { attendance: 1.0 },
			},
		],
		slot6: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
				metadata: { attendance: 1.0 },
			},
		],
		slot7: [],
		slot8: [],
		slot9: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
				metadata: { attendance: 1.0 },
			},
		],
		slot10: [],
		slot11: [],
	},
	wednesday: {
		slot1: [],
		slot2: [],
		slot3: [],
		slot4: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
				metadata: { attendance: 1.0 },
			},
		],
		slot5: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
				metadata: { attendance: 1.0 },
			},
		],
		slot6: [],
		slot7: [],
		slot8: [],
		slot9: [],
		slot10: [],
		slot11: [],
	},
	thursday: {
		slot1: [],
		slot2: [
			{
				class: { id: 21738, code: "4;PTC;3G", name: "Personal Tutor" },
				teacher: {
					id: 2486,
					initials: "KLF",
					firstName: "Karina",
					surname: "Free",
				},
				room: "938",
				metadata: { attendance: 1.0 },
			},
		],
		slot3: [],
		slot4: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
				metadata: { attendance: 1.0 },
			},
		],
		slot5: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
				metadata: { attendance: 1.0 },
			},
		],
		slot6: [
			{
				class: { id: 21437, code: "4;ECB;B1", name: "Economics" },
				teacher: {
					id: 4011,
					initials: "OAS",
					firstName: "Stefania",
					surname: "Spinu",
				},
				room: "208",
				metadata: { attendance: 1.0 },
			},
		],
		slot7: [],
		slot8: [],
		slot9: [
			{
				class: { id: 21437, code: "4;ECB;B1", name: "Economics" },
				teacher: {
					id: 4011,
					initials: "OAS",
					firstName: "Stefania",
					surname: "Spinu",
				},
				room: "208",
				metadata: { attendance: 1.0 },
			},
		],
		slot10: [],
		slot11: [],
	},
	friday: {
		slot1: [],
		slot2: [],
		slot3: [],
		slot4: [],
		slot5: [],
		slot6: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
				metadata: { attendance: 1.0 },
			},
		],
		slot7: [],
		slot8: [],
		slot9: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
				metadata: { attendance: 1.0 },
			},
		],
		slot10: [],
		slot11: [],
	},
};

const staffTimetable = {
	monday: {
		slot1: [],
		slot2: [],
		slot3: [],
		slot4: [
			{
				class: { id: 22073, code: "0;XSN;C1", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22345, code: "0;XSN;X2", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22353, code: "0;XSN;X3", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22356, code: "0;XSN;X6", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22450, code: "0;XSN;XD", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22596, code: "0;XSN;XH", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
		],
		slot5: [
			{
				class: { id: 22073, code: "0;XSN;C1", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22345, code: "0;XSN;X2", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22353, code: "0;XSN;X3", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22356, code: "0;XSN;X6", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22450, code: "0;XSN;XD", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22596, code: "0;XSN;XH", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
		],
		slot6: [
			{
				class: { id: 22075, code: "3;PTC;4G", name: "Personal Tutor" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "321",
			},
		],
		slot7: [],
		slot8: [],
		slot9: [],
		slot10: [],
		slot11: [],
	},
	tuesday: {
		slot1: [],
		slot2: [],
		slot3: [],
		slot4: [],
		slot5: [],
		slot6: [],
		slot7: [],
		slot8: [],
		slot9: [],
		slot10: [
			{
				class: { id: 22077, code: "4;PTC;1T", name: "Personal Tutor" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "321",
			},
		],
		slot11: [
			{
				class: { id: 22076, code: "3;PTC;4H", name: "Personal Tutor" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "321",
			},
		],
	},
	wednesday: {
		slot1: [
			{
				class: { id: 22074, code: "0;XSN;D1", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22344, code: "0;XSN;X1", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22345, code: "0;XSN;X2", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22355, code: "0;XSN;X5", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22444, code: "0;XSN;XC", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22451, code: "0;XSN;XE", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22596, code: "0;XSN;XH", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
		],
		slot2: [
			{
				class: { id: 22074, code: "0;XSN;D1", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22344, code: "0;XSN;X1", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22345, code: "0;XSN;X2", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22355, code: "0;XSN;X5", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22444, code: "0;XSN;XC", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22451, code: "0;XSN;XE", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
			{
				class: { id: 22596, code: "0;XSN;XH", name: "Extra Support" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "236",
			},
		],
		slot3: [],
		slot4: [],
		slot5: [
			{
				class: { id: 22078, code: "4;PTC;1P", name: "Personal Tutor" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "317",
			},
		],
		slot6: [],
		slot7: [],
		slot8: [],
		slot9: [],
		slot10: [
			{
				class: { id: 22338, code: "0;DCN;F1", name: "Misconduct Workshop" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "318",
			},
		],
		slot11: [],
	},
	thursday: {
		slot1: [],
		slot2: [],
		slot3: [],
		slot4: [],
		slot5: [],
		slot6: [],
		slot7: [],
		slot8: [],
		slot9: [],
		slot10: [],
		slot11: [],
	},
	friday: {
		slot1: [
			{
				class: { id: 22288, code: "0;X71;01", name: "+Explore" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "321",
			},
		],
		slot2: [
			{
				class: { id: 22288, code: "0;X71;01", name: "+Explore" },
				teacher: {
					id: 3903,
					initials: "MFW",
					firstName: "Mylz",
					surname: "Furlonge-Walker",
				},
				room: "321",
			},
		],
		slot3: [],
		slot4: [],
		slot5: [],
		slot6: [],
		slot7: [],
		slot8: [],
		slot9: [],
		slot10: [],
		slot11: [],
	},
};

const roomTimetable = {
	monday: {
		slot1: [
			{
				class: { id: 21581, code: "4;TT8;AC", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
		slot2: [
			{
				class: { id: 21581, code: "4;TT8;AC", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
		slot3: [],
		slot4: [
			{
				class: { id: 21690, code: "3;TT8;CE", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
		slot5: [
			{
				class: { id: 21690, code: "3;TT8;CE", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
		slot6: [
			{
				class: { id: 21575, code: "4;ST4;D1", name: "Sport" },
				teacher: {
					id: 3707,
					initials: "JTB",
					firstName: "Jonny",
					surname: "Burden",
				},
				room: "402",
			},
		],
		slot7: [],
		slot8: [],
		slot9: [
			{
				class: { id: 21575, code: "4;ST4;D1", name: "Sport" },
				teacher: {
					id: 3707,
					initials: "JTB",
					firstName: "Jonny",
					surname: "Burden",
				},
				room: "402",
			},
		],
		slot10: [
			{
				class: { id: 21692, code: "3;TT4;B1", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
		slot11: [
			{
				class: { id: 21692, code: "3;TT4;B1", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
	},
	tuesday: {
		slot1: [
			{
				class: { id: 21694, code: "3;TT4;E1", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
		slot2: [
			{
				class: { id: 21694, code: "3;TT4;E1", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
		slot3: [],
		slot4: [
			{
				class: { id: 21692, code: "3;TT4;B1", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
		slot5: [
			{
				class: { id: 21692, code: "3;TT4;B1", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
		slot6: [
			{
				class: { id: 21581, code: "4;TT8;AC", name: "Travel and Tourism" },
				teacher: {
					id: 2487,
					initials: "HMS",
					firstName: "Helen",
					surname: "Sharp",
				},
				room: "402",
			},
		],
		slot7: [],
		slot8: [],
		slot9: [
			{
				class: { id: 21581, code: "4;TT8;AC", name: "Travel and Tourism" },
				teacher: {
					id: 2487,
					initials: "HMS",
					firstName: "Helen",
					surname: "Sharp",
				},
				room: "402",
			},
		],
		slot10: [],
		slot11: [],
	},
	wednesday: {
		slot1: [
			{
				class: { id: 21629, code: "3;BSB;M2", name: "Business" },
				teacher: {
					id: 3949,
					initials: "VCB",
					firstName: "Vikki",
					surname: "Barnard",
				},
				room: "402",
			},
		],
		slot2: [
			{
				class: { id: 21629, code: "3;BSB;M2", name: "Business" },
				teacher: {
					id: 3949,
					initials: "VCB",
					firstName: "Vikki",
					surname: "Barnard",
				},
				room: "402",
			},
		],
		slot3: [],
		slot4: [
			{
				class: { id: 21694, code: "3;TT4;E1", name: "Travel and Tourism" },
				teacher: {
					id: 3679,
					initials: "SON",
					firstName: "Sofia",
					surname: "Neves De Abreu",
				},
				room: "402",
			},
		],
		slot5: [
			{
				class: { id: 21694, code: "3;TT4;E1", name: "Travel and Tourism" },
				teacher: {
					id: 3679,
					initials: "SON",
					firstName: "Sofia",
					surname: "Neves De Abreu",
				},
				room: "402",
			},
		],
		slot6: [
			{
				class: { id: 21737, code: "4;PTC;5E", name: "Personal Tutor" },
				teacher: {
					id: 1025,
					initials: "SNG",
					firstName: "Steve",
					surname: "Grantham",
				},
				room: "402",
			},
		],
		slot7: [],
		slot8: [],
		slot9: [],
		slot10: [],
		slot11: [],
	},
	thursday: {
		slot1: [
			{
				class: { id: 21690, code: "3;TT8;CE", name: "Travel and Tourism" },
				teacher: {
					id: 3679,
					initials: "SON",
					firstName: "Sofia",
					surname: "Neves De Abreu",
				},
				room: "402",
			},
		],
		slot2: [
			{
				class: { id: 21690, code: "3;TT8;CE", name: "Travel and Tourism" },
				teacher: {
					id: 3679,
					initials: "SON",
					firstName: "Sofia",
					surname: "Neves De Abreu",
				},
				room: "402",
			},
		],
		slot3: [],
		slot4: [
			{
				class: { id: 21578, code: "4;TT4;A1", name: "Travel and Tourism" },
				teacher: {
					id: 2487,
					initials: "HMS",
					firstName: "Helen",
					surname: "Sharp",
				},
				room: "402",
			},
		],
		slot5: [
			{
				class: { id: 21578, code: "4;TT4;A1", name: "Travel and Tourism" },
				teacher: {
					id: 2487,
					initials: "HMS",
					firstName: "Helen",
					surname: "Sharp",
				},
				room: "402",
			},
		],
		slot6: [
			{
				class: { id: 21692, code: "3;TT4;B1", name: "Travel and Tourism" },
				teacher: {
					id: 2487,
					initials: "HMS",
					firstName: "Helen",
					surname: "Sharp",
				},
				room: "402",
			},
		],
		slot7: [],
		slot8: [],
		slot9: [
			{
				class: { id: 21692, code: "3;TT4;B1", name: "Travel and Tourism" },
				teacher: {
					id: 2487,
					initials: "HMS",
					firstName: "Helen",
					surname: "Sharp",
				},
				room: "402",
			},
		],
		slot10: [
			{
				class: { id: 21632, code: "3;BSB;M1", name: "Business" },
				teacher: {
					id: 3949,
					initials: "VCB",
					firstName: "Vikki",
					surname: "Barnard",
				},
				room: "402",
			},
		],
		slot11: [
			{
				class: { id: 21632, code: "3;BSB;M1", name: "Business" },
				teacher: {
					id: 3949,
					initials: "VCB",
					firstName: "Vikki",
					surname: "Barnard",
				},
				room: "402",
			},
		],
	},
	friday: {
		slot1: [],
		slot2: [],
		slot3: [],
		slot4: [],
		slot5: [],
		slot6: [
			{
				class: { id: 21694, code: "3;TT4;E1", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
		slot7: [],
		slot8: [],
		slot9: [
			{
				class: { id: 21694, code: "3;TT4;E1", name: "Travel and Tourism" },
				teacher: {
					id: 636,
					initials: "JES",
					firstName: "James",
					surname: "Shepherd",
				},
				room: "402",
			},
		],
		slot10: [
			{
				class: { id: 21581, code: "4;TT8;AC", name: "Travel and Tourism" },
				teacher: {
					id: 3679,
					initials: "SON",
					firstName: "Sofia",
					surname: "Neves De Abreu",
				},
				room: "402",
			},
		],
		slot11: [
			{
				class: { id: 21581, code: "4;TT8;AC", name: "Travel and Tourism" },
				teacher: {
					id: 3679,
					initials: "SON",
					firstName: "Sofia",
					surname: "Neves De Abreu",
				},
				room: "402",
			},
		],
	},
};

const classTimetable = {
	monday: {
		slot1: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
			},
		],
		slot2: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
			},
		],
		slot3: [],
		slot4: [],
		slot5: [],
		slot6: [],
		slot7: [],
		slot8: [],
		slot9: [],
		slot10: [],
		slot11: [],
	},
	tuesday: {
		slot1: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
			},
		],
		slot2: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
			},
		],
		slot3: [],
		slot4: [],
		slot5: [],
		slot6: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
			},
		],
		slot7: [],
		slot8: [],
		slot9: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
			},
		],
		slot10: [],
		slot11: [],
	},
	wednesday: {
		slot1: [],
		slot2: [],
		slot3: [],
		slot4: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
			},
		],
		slot5: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
			},
		],
		slot6: [],
		slot7: [],
		slot8: [],
		slot9: [],
		slot10: [],
		slot11: [],
	},
	thursday: {
		slot1: [],
		slot2: [],
		slot3: [],
		slot4: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
			},
		],
		slot5: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 2474,
					initials: "SAB",
					firstName: "Seonaid",
					surname: "Botfield",
				},
				room: "201",
			},
		],
		slot6: [],
		slot7: [],
		slot8: [],
		slot9: [],
		slot10: [],
		slot11: [],
	},
	friday: {
		slot1: [],
		slot2: [],
		slot3: [],
		slot4: [],
		slot5: [],
		slot6: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
			},
		],
		slot7: [],
		slot8: [],
		slot9: [
			{
				class: { id: 21410, code: "4;BS8;AE", name: "Business" },
				teacher: {
					id: 3680,
					initials: "BMK",
					firstName: "Brenda",
					surname: "Kerr",
				},
				room: "201",
			},
		],
		slot10: [],
		slot11: [],
	},
};
